import React, { useEffect, useRef, useState } from 'react';
import { X, Minimize2, Send, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService } from '../../services/dataService';

interface SupportMessage {
  id: string;
  sender: 'user' | 'admin';
  content: string;
  timestamp: any;
  read: boolean;
}

const SupportChatBubble: React.FC = () => {
  const { tenantData, userData } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Subscribe to messages
  useEffect(() => {
    if (!tenantData?.id || !isOpen) return;
    const unsubscribe = dataService.subscribeToSupportChat(tenantData.id, (msgs: SupportMessage[]) => {
      setMessages(msgs.sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds));
      setLoading(false);
    });
    return unsubscribe;
  }, [tenantData?.id, isOpen]);

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('open-support-chat', handleOpen);
    return () => window.removeEventListener('open-support-chat', handleOpen);
  }, []);

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() || !tenantData?.id || !userData) return;
    setSending(true);
    try {
      await dataService.sendSupportMessage(tenantData.id, {
        sender: 'user',
        content: input.trim(),
        timestamp: new Date(),
        read: false,
        userId: userData.id,
        userName: userData.firstName + ' ' + userData.lastName
      });
      setInput('');
    } catch (err) {
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  // Chat bubble
  return (
    <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-2 tablet:bottom-6 tablet:right-6 z-50 w-full max-w-sm sm:max-w-md">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col h-[70vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 bg-primary-600 rounded-t-lg">
          <div className="flex items-center gap-2 text-white font-semibold">
            <Shield className="h-5 w-5" />
            Support
          </div>
          <div className="flex gap-2">
            <button onClick={() => setIsMinimized(true)} className="hover:bg-primary-700/30 rounded p-1">
              <Minimize2 className="h-5 w-5 text-white" />
            </button>
            <button onClick={() => setIsOpen(false)} className="hover:bg-primary-700/30 rounded p-1">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
        {/* Chat area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
          {loading ? (
            <div className="text-center text-gray-400 dark:text-gray-500">Loading...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-400 dark:text-gray-500">No messages yet. Start the conversation!</div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`rounded-lg px-4 py-2 max-w-xs break-words shadow-sm ${
                  msg.sender === 'user' 
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}>
                  <div>{msg.content}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleString() : new Date(msg.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <form onSubmit={handleSend} className="p-4 border-t border-gray-100 dark:border-gray-700 flex gap-2">
          <input
            type="text"
            className="flex-1 input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            placeholder="Type your message..."
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={sending}
          />
          <button type="submit" className="btn-primary flex items-center" disabled={sending || !input.trim()}>
            <Send className="h-5 w-5 mr-1" /> Send
          </button>
        </form>
      </div>
              {/* Minimized bubble */}
      {isMinimized && (
        <button
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-2 tablet:bottom-6 tablet:right-6 z-50 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg p-4 flex items-center focus:outline-none"
          onClick={() => { setIsOpen(true); setIsMinimized(false); }}
          title="Open Support Chat"
        >
          <Shield className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default SupportChatBubble; 